import { useState } from 'react'
import type { DraftState } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import type { SequencingPlatformKey } from '../../../Generated/SequencingPlatform.ts'
import type { IssueList } from '../../../Services/OrderWizard/issues.ts'
import { submitBlockedReason } from '../../../Services/OrderWizard/issues.ts'
import { isBusy } from '../../../Services/OrderWizard/writeState.ts'
import { currentSubmitFlow, reviewChecking } from '../../../Services/OrderWizard/stepSummaries.ts'
import { filesAtHandOf } from '../../../Flux/Selector/OrderWizard/OrderWizardSelectors.ts'
import { isEarlyAccessSequencingPlatform, needsSequencingPlatformChoice } from '../../../Util/sequencingPlatform.ts'
import {
  BlockedReason,
  CheckAgain,
  ReviewDetails,
  ReviewGate,
  SettingsSummary,
  SubmitAgreements,
  SubmittedNotice,
  WarningNote
} from './ReviewParts.tsx'
import { useReviewSession } from './useReviewSession.ts'
import { useStepSelect } from './stepSelect.ts'
import { useDispatch } from '../../../Flux/index.ts'
import { orderWizardActions as wizard } from '../../../Flux/Slice/OrderWizard/OrderWizardSlice.ts'
import { MissingReverseList } from './ReverseFiles.tsx'

/**
 * The classic submit's checks, previews, agreements and call, inside the wizard. The checks run on their
 * own, reading only, for the draft as it is. Amplicon min/max length and admin pipeline overrides stay
 * in the classic view.
 */
export const ReviewAndSubmit = ({
  draft,
  orderId,
  issues,
  onShowIssues
}: {
  draft: DraftState
  orderId: string
  issues: IssueList
  onShowIssues: () => void
}) => {
  const session = useReviewSession(draft, onShowIssues)
  const dispatch = useDispatch()
  const select = useStepSelect()
  const toPrimers = () => (select ? select('primers') : dispatch(wizard.stepChanged({ draftId: draft.id, step: 'primers' })))
  const [agreement, setAgreement] = useState(false)
  const [terms, setTerms] = useState(false)
  const [earlyAccessAcknowledged, setEarlyAccessAcknowledged] = useState(false)
  const filesAtHand = filesAtHandOf(draft)
  const blockedReason = submitBlockedReason(issues, isBusy(draft, filesAtHand))
  /* the footer states the blockers; the body says only what else holds submit */
  const bodyReason = issues.blockers.length > 0 ? undefined : blockedReason
  const current = currentSubmitFlow(draft)
  const override = (platform: SequencingPlatformKey | null) => {
    setEarlyAccessAcknowledged(false)
    session.override(platform)
  }

  if (draft.submission) {
    return <SubmittedNotice orderId={orderId} />
  }
  const review = current?.review
  if (!current || !review) {
    return (
      <>
        <MissingReverseList draft={draft} />
        <ReviewGate
          draft={draft}
          issues={issues}
          checking={reviewChecking(draft, filesAtHand)}
          failure={current?.stage === 'failed' ? current.message : undefined}
          refusal={current?.stage === 'checked' && !blockedReason ? current.message : undefined}
          blockedReason={bodyReason}
          checked={current?.stage === 'checked'}
          onRetry={session.retry}
          onShowIssues={onShowIssues}
        />
      </>
    )
  }

  const stage = current.stage
  const overrideInFlight = !!current.overrideInFlight
  const submitIssues = current.issues ?? []
  const platformIssues = review.platform?.issues ?? []
  const passed = !blockedReason && platformIssues.length === 0 && !needsSequencingPlatformChoice(review.platform)
  const earlyAccessBlocks =
    isEarlyAccessSequencingPlatform(review.platform?.effectiveSequencingPlatform ?? null) && !earlyAccessAcknowledged
  return (
    <section className="space-y-3" data-testid="wizard-review">
      <MissingReverseList draft={draft} />
      <SettingsSummary draft={draft} />
      <ReviewDetails
        review={review}
        orderId={orderId}
        warningNote={<WarningNote issues={issues} onShowIssues={onShowIssues} />}
        overrideInFlight={overrideInFlight}
        onOverride={override}
        earlyAccessAcknowledged={earlyAccessAcknowledged}
        onEarlyAccessAcknowledged={setEarlyAccessAcknowledged}
        onCheckPrimers={toPrimers}
      />
      <SubmitAgreements agreement={agreement} terms={terms} disabled={!passed} onAgreement={setAgreement} onTerms={setTerms} />
      {submitIssues.length > 0 && (
        <ul className="text-sm text-error" data-testid="wizard-submit-issues">
          {submitIssues.map((issue, index) => (
            <li key={index}>{issue}</li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="btn btn-primary btn-sm"
          data-testid="wizard-submit"
          disabled={!passed || !agreement || !terms || overrideInFlight || earlyAccessBlocks || stage === 'submitting'}
          onClick={session.submit}
        >
          {stage === 'submitting' ? 'Submitting…' : 'Submit to processing'}
        </button>
        {bodyReason && <BlockedReason reason={bodyReason} issues={issues} onShowIssues={onShowIssues} />}
        {stage === 'checked' && !overrideInFlight && <CheckAgain onClick={session.retry} />}
      </div>
    </section>
  )
}
